import {I18n} from 'aws-amplify/utils';
import {
    Alert,
    Badge,
    Button,
    Code,
    Group,
    List,
    Modal,
    Stack,
    Text,
} from '@mantine/core';

import {
    Certificate,
    DeleteCertificateResult,
} from '../types';
// FNP-1280 — the shared classifier directly; the certificate-specific
// wrapper that string-matched the backend's refusal prose is deleted, because
// the backend now sends the kind in the message prefix.
import {classifyWriteError} from '../../zones/services/writeErrors';
import {
    certificateWriteErrorCopy,
    deleteRefusedCopy,
} from '../writeErrorMessages';
// FNP-1365 — the ask for `acm:DeleteCertificate`, at the moment it is needed.
import {GrantElevationPrompt} from '../../provider-connections/components/GrantElevationPrompt';
import {useCertificateGrantElevation} from '../hooks/useCertificateGrantElevation';
import {productCopy} from '../../../config/productCopy';

// FNP-1193 — THE DELETE CONFIRMATION, AND THE PROVIDER'S REFUSAL.
//
// ⚠️ A CONTROLLED Mantine `Modal`, NOT `modals.openConfirmModal`.
//
// @mantine/modals is a dependency but NO `ModalsProvider` is mounted in
// src/App.tsx, so `modals.openConfirmModal` silently does nothing — the worst
// possible failure for a destructive-action guard, because the button would
// appear inert rather than broken. The zones and records modules reached the
// same conclusion for the same reason.
//
// ⚠️ THIS MODAL HAS THREE STATES, NOT TWO. Confirm, refused, and failed.
//
// "Refused" is the one that does not exist elsewhere in this app: the provider
// was reached, it answered, and its answer was no. That is not an error — it is
// the certificate doing its job — and rendering it as a generic failure would
// lose both the reason and the fact that nothing is broken.
export const DeleteCertificateModal = (
    {
        certificate,
        opened,
        onClose,
        onConfirm,
        isDeleting = false,
        refusal,
        error,
    }: {
        certificate?: Certificate,
        opened: boolean,
        onClose: () => void,
        onConfirm: () => void,
        isDeleting?: boolean,
        // ⚠️ THE PROVIDER'S "NO", as a resolved result. Never an exception.
        refusal?: DeleteCertificateResult,
        // A genuine transport or authorisation failure — distinct from a
        // refusal, because one means the provider spoke and the other means it
        // was never reached.
        error?: unknown,
    },
) => {

    // FNP-1365 — asked BEFORE the attempt, from the stored declaration, so this
    // is an offer rather than an apology. Hooks run unconditionally; the early
    // return below guards the render.
    const elevation = useCertificateGrantElevation(certificate?.connectionId);

    if (!certificate) {
        return null;
    }

    const writeError = error ? classifyWriteError(error) : undefined;
    const covered = certificate.coveredNames ?? [];

    // ⚠️ A MISSING GRANT DISABLES THE CONFIRM, like an in-use refusal does.
    // Without `acm:DeleteCertificate` the API refuses on `canDelete` before any
    // AWS call, so the click cannot succeed — offering it would be the failure
    // the prompt above replaces.
    const permissionMissing = !elevation.granted
        && (elevation.shouldPrompt || elevation.declined);

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={I18n.get('Delete certificate')}
            size={'lg'}
            centered={true}
        >
            <Stack gap={'md'}>

                <Stack gap={4}>
                    <Text size={'sm'}>
                        {/* The title already says "Delete certificate". */}
                        {I18n.get('Deleting:')}
                    </Text>
                    <Group gap={'xs'}>
                        <Code>{certificate.domainName}</Code>
                        <Badge variant={'light'}>{certificate.region}</Badge>
                    </Group>
                    {/* ⚠️ NAMES EVERYTHING IT COVERS. "Delete 1 item?" is a
                        rubber stamp — the operator already knows they clicked
                        delete. What they may not know is that this certificate
                        also covers three other hostnames that are serving
                        traffic right now. */}
                    {covered.length > 1 && (
                        <Stack gap={2}>
                            <Text size={'xs'} c={'dimmed'}>
                                {I18n.get('This certificate covers:')}
                            </Text>
                            <List size={'xs'} c={'dimmed'}>
                                {covered.map(name => (
                                    <List.Item key={name}>{name}</List.Item>
                                ))}
                            </List>
                        </Stack>
                    )}
                </Stack>

                {/* ─── FNP-1365: THE PERMISSION THEY WITHHELD ───

                    ⚠️ ABOVE THE REFUSAL AND ERROR STATES, because it is the
                    reason this delete cannot proceed at all, and because unlike
                    those two it is answerable right here. */}
                {elevation.shouldPrompt && (
                    <GrantElevationPrompt
                        grant={'deleteCertificates'}
                        currentSelection={elevation.currentSelection}
                        onGrant={elevation.grantIt}
                        onDecline={elevation.decline}
                        isSaving={elevation.isSaving}
                        // Names THIS certificate. ACM deletes by ARN and an
                        // operator with several certificates on one domain
                        // needs to know which.
                        manualAlternative={`Delete the certificate for ${certificate.domainName} in the ACM console for ${certificate.region}, then remove it here.`}
                    />
                )}

                {/* ⚠️ THE DECLINE PATH IS NOT A DEAD END. They cannot delete
                    through Flexi, so they get the way to do it themselves. */}
                {elevation.declined && !elevation.granted && (
                    <Alert
                        color={'gray'}
                        variant={'light'}
                        title={I18n.get('Delete this certificate in AWS')}
                        data-testid={'elevation-declined-certificate'}
                    >
                        <Text size={'sm'}>
                            {/* ⚠️ THE DECLINE PATH IS NOT A DEAD END, so the
                                destination stays. Cut from the screen: "and
                                will not ask again" (the prompt is already
                                gone, which says it) and "it will stop
                                appearing here" (the consequence of deleting a
                                thing, not news). */}
                            {productCopy('{product} cannot delete certificates for you. Delete it in the ACM console.')}
                        </Text>
                    </Alert>
                )}

                {/* ⚠️ THE IN-USE REFUSAL. Rendered as INFORMATION, not as a
                    failure: colour and wording both say the provider told us
                    something useful and the certificate is unharmed. */}
                {refusal && (
                    <Alert
                        color={refusal.inUse ? 'yellow' : 'red'}
                        variant={'light'}
                        title={deleteRefusedCopy(refusal).title}
                    >
                        <Stack gap={'xs'}>
                            <Text size={'sm'}>
                                {deleteRefusedCopy(refusal).action}
                            </Text>
                            {/* The provider's own words, VERBATIM. This is the
                                sentence the ticket asks to be rendered rather
                                than a generic error. */}
                            {refusal.reason && (
                                <Text size={'xs'} c={'dimmed'}>
                                    {refusal.reason}
                                </Text>
                            )}
                            {/* The local row survives a refusal, and saying so
                                stops an operator wondering whether the console
                                and the provider now disagree. */}
                            {!refusal.localRecordDeleted && (
                                <Text size={'xs'} c={'dimmed'}>
                                    {I18n.get('The certificate is still listed here, because it still exists.')}
                                </Text>
                            )}
                        </Stack>
                    </Alert>
                )}

                {/* A failure to REACH the provider. Different from a refusal
                    and worded so the two are not confusable. */}
                {writeError && (
                    <Alert
                        color={'red'}
                        variant={'light'}
                        title={certificateWriteErrorCopy(writeError.kind).title}
                    >
                        <Stack gap={'xs'}>
                            <Text size={'sm'}>
                                {certificateWriteErrorCopy(writeError.kind).action}
                            </Text>
                            {writeError.providerMessage && (
                                <Text size={'xs'} c={'dimmed'}>
                                    {writeError.providerMessage}
                                </Text>
                            )}
                            <Code>{writeError.kind}</Code>
                        </Stack>
                    </Alert>
                )}

                {/* ⚠️ SAYS THE PROVIDER DECIDES, up front, so a refusal is not a
                    surprise. Flexi does not pre-check against local state:
                    local state goes stale and the cost of being wrong is a site
                    that stops serving. */}
                <Text size={'sm'}>
                    {/* ⚠️ FNP-1439 — THE MIDDLE SENTENCE IS GONE FROM THE
                        SCREEN, NOT THE BEHAVIOUR. It read: "If something is
                        still serving traffic with it, the deletion will be
                        refused and nothing will change." That is a prediction
                        of an outcome the operator will be shown when it
                        happens — the in-use refusal alert above renders the
                        provider's own reason, which is both more specific and
                        actually true at the time it appears. What has to be
                        said BEFORE the click is who decides and that it is
                        permanent. */}
                    {I18n.get('The provider decides. Cannot be undone once it succeeds.')}
                </Text>

                <Group justify={'flex-end'}>
                    <Button variant={'default'} onClick={onClose} disabled={isDeleting}>
                        {/* After a refusal the only sensible action is to leave,
                            so the cancel button says so. */}
                        {refusal ? I18n.get('Close') : I18n.get('Cancel')}
                    </Button>
                    <Button
                        color={'red'}
                        onClick={onConfirm}
                        loading={isDeleting}
                        // ⚠️ DISABLED AFTER AN IN-USE REFUSAL. Retrying the
                        // identical request against a provider that just
                        // explained why it said no is not a recovery, it is a
                        // second identical refusal. The operator has to go and
                        // detach the resource first.
                        disabled={isDeleting || Boolean(refusal?.inUse) || permissionMissing}
                    >
                        {I18n.get('Delete certificate')}
                    </Button>
                </Group>

            </Stack>
        </Modal>
    );
};
